import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Check, X, RotateCcw, ArrowRight, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { formatVariantLabel } from "@/lib/variants";

// Backend QUOTE_CLOSE_REASONS ile birebir aynı liste (reuse)
const REASONS = ["Fiyat", "Rakip Firma", "Termin", "Müşteri Vazgeçti", "Ödeme Şartları", "Proje Ertelendi", "Diğer"];

const STATUS_META = {
  onaylandi: { label: "Onaylandı", cls: "bg-emerald-950/40 border-emerald-900/60 text-emerald-300" },
  reddedildi: { label: "Reddedildi", cls: "bg-rose-950/40 border-rose-900/60 text-rose-300" },
  bekliyor: { label: "Bekliyor", cls: "bg-slate-900 border-slate-700 text-slate-400" },
};

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function QuoteLineApprovalDialog({ order, open, onOpenChange, onChanged }) {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyLine, setBusyLine] = useState("");
  const [rejecting, setRejecting] = useState(""); // line_id
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (!open || !order?.id) return;
    setLoading(true);
    setFull(null);
    setRejecting("");
    (async () => {
      try {
        const { data } = await api.get(`/orders/${order.id}`);
        setFull(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, order?.id]);

  const lines = full?.line_items || [];
  const summary = useMemo(() => {
    const approved = lines.filter((l) => l.approval_status === "onaylandi").length;
    const rejected = lines.filter((l) => l.approval_status === "reddedildi").length;
    return { total: lines.length, approved, rejected, pending: lines.length - approved - rejected };
  }, [lines]);
  const convertible = lines.filter((l) => l.approval_status === "onaylandi" && !l.converted_order_id);

  const setApproval = async (lineId, status, rejectReason, rejectNote) => {
    setBusyLine(lineId);
    try {
      const { data } = await api.patch(`/orders/${order.id}/lines/${lineId}/approval`, {
        status,
        reject_reason: rejectReason || null,
        reject_note: rejectNote || null,
      });
      setFull(data);
      setRejecting("");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyLine("");
    }
  };

  const submitReject = (lineId) => {
    if (!reason) { toast.error("Red sebebi seçimi zorunludur"); return; }
    if (reason === "Diğer" && !note.trim()) { toast.error("'Diğer' sebebi için açıklama zorunludur"); return; }
    setApproval(lineId, "reddedildi", reason, note.trim());
  };

  const convert = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/convert-approved`);
      if (data.mode === "full") {
        toast.success("Teklif siparişe dönüştürüldü");
      } else {
        toast.success(`${data.converted} kalem siparişe dönüştürüldü (${data.order_no}) · ${data.remaining} kalem teklifte kaldı`);
      }
      onChanged?.();
      if (data.mode === "full") {
        onOpenChange(false);
      } else {
        const { data: refreshed } = await api.get(`/orders/${order.id}`);
        setFull(refreshed);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConverting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-slate-900 border-slate-800" data-testid="line-approval-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50 flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-cyan-400" />
            Kalem Onayı — {order?.order_no}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Yükleniyor…
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2" data-testid="line-approval-summary">
              <Badge variant="outline" className="bg-slate-950 border-slate-700 text-slate-300 font-mono text-[10px] uppercase">
                Toplam {summary.total} kalem
              </Badge>
              <Badge variant="outline" className="bg-emerald-950/40 border-emerald-900/60 text-emerald-300 font-mono text-[10px] uppercase" data-testid="line-approval-summary-approved">
                Onaylandı {summary.approved}
              </Badge>
              <Badge variant="outline" className="bg-rose-950/40 border-rose-900/60 text-rose-300 font-mono text-[10px] uppercase" data-testid="line-approval-summary-rejected">
                Reddedildi {summary.rejected}
              </Badge>
              <Badge variant="outline" className="bg-slate-900 border-slate-700 text-slate-400 font-mono text-[10px] uppercase" data-testid="line-approval-summary-pending">
                Bekliyor {summary.pending}
              </Badge>
            </div>

            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
              {lines.map((l, i) => {
                const st = l.converted_order_id ? null : (STATUS_META[l.approval_status || "bekliyor"] || STATUS_META.bekliyor);
                const busy = busyLine === l.id;
                return (
                  <div key={l.id || i} data-testid={`line-approval-row-${l.id || i}`}
                    className="rounded-sm border border-slate-800 bg-slate-950/40 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-slate-100 truncate">{l.product_name}</div>
                        <div className="text-[11px] text-slate-500 font-mono truncate">
                          {formatVariantLabel(l.variant_attributes, l.variant_sku || "—")}
                        </div>
                        <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                          {l.quantity} adet · ₺{fmt(l.line_total ?? l.total_price)}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {l.converted_order_id ? (
                          <Badge variant="outline" data-testid={`line-converted-badge-${l.id}`}
                            className="bg-blue-950/40 border-blue-900/60 text-blue-300 font-mono text-[10px] uppercase">
                            Siparişe Dönüştürüldü
                          </Badge>
                        ) : (
                          <Badge variant="outline" data-testid={`line-status-badge-${l.id}`}
                            className={`font-mono text-[10px] uppercase ${st.cls}`}>
                            {st.label}
                          </Badge>
                        )}
                        {l.approval_status === "reddedildi" && l.reject_reason && (
                          <div className="text-[10px] text-rose-400/80 max-w-[220px] text-right" data-testid={`line-reject-reason-${l.id}`}>
                            {l.reject_reason}{l.reject_note ? ` — ${l.reject_note}` : ""}
                          </div>
                        )}
                      </div>
                    </div>

                    {!l.converted_order_id && (
                      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-800/60">
                        {l.approval_status !== "onaylandi" && (
                          <Button size="sm" variant="ghost" disabled={busy}
                            data-testid={`line-approve-${l.id}`}
                            onClick={() => setApproval(l.id, "onaylandi")}
                            className="h-7 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-950/40">
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                            Onayla
                          </Button>
                        )}
                        {l.approval_status !== "reddedildi" && (
                          <Button size="sm" variant="ghost" disabled={busy}
                            data-testid={`line-reject-${l.id}`}
                            onClick={() => { setRejecting(rejecting === l.id ? "" : l.id); setReason(""); setNote(""); }}
                            className="h-7 px-2 text-rose-400 hover:text-rose-300 hover:bg-rose-950/40">
                            <X className="w-3.5 h-3.5 mr-1" />
                            Reddet
                          </Button>
                        )}
                        {l.approval_status && l.approval_status !== "bekliyor" && (
                          <Button size="sm" variant="ghost" disabled={busy}
                            data-testid={`line-reset-${l.id}`}
                            onClick={() => setApproval(l.id, "bekliyor")}
                            className="h-7 px-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800">
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                            Beklemeye Al
                          </Button>
                        )}
                      </div>
                    )}

                    {rejecting === l.id && (
                      <div className="mt-2 rounded-sm border border-rose-900/50 bg-rose-950/20 p-2.5 space-y-2" data-testid={`line-reject-form-${l.id}`}>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Red Sebebi *</div>
                          <Select value={reason} onValueChange={setReason}>
                            <SelectTrigger data-testid={`line-reject-reason-select-${l.id}`}
                              className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm h-8 text-xs">
                              <SelectValue placeholder="Sebep seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              {REASONS.map((r) => (
                                <SelectItem key={r} value={r} data-testid={`line-reject-reason-option-${r}`}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">
                            Açıklama {reason === "Diğer" ? "*" : "(opsiyonel)"}
                          </div>
                          <Input value={note} onChange={(e) => setNote(e.target.value)}
                            data-testid={`line-reject-note-${l.id}`}
                            className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm h-8 text-xs"
                            placeholder="Kısa açıklama" />
                        </div>
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" variant="ghost" onClick={() => setRejecting("")}
                            data-testid={`line-reject-cancel-${l.id}`}
                            className="h-7 px-2 text-slate-400 hover:text-slate-200">
                            Vazgeç
                          </Button>
                          <Button size="sm" disabled={busyLine === l.id}
                            data-testid={`line-reject-submit-${l.id}`}
                            onClick={() => submitReject(l.id)}
                            className="h-7 px-3 bg-rose-600 hover:bg-rose-500 text-white">
                            {busyLine === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Reddet"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <DialogFooter className="flex items-center justify-between sm:justify-between gap-2">
          <div className="text-[11px] text-slate-500 font-mono" data-testid="line-approval-convertible-count">
            {convertible.length > 0 ? `${convertible.length} onaylı kalem dönüşüm bekliyor` : "Dönüştürülecek onaylı kalem yok"}
          </div>
          <Button disabled={converting || loading || convertible.length === 0}
            data-testid="convert-approved-submit"
            onClick={convert}
            className="bg-emerald-600 hover:bg-emerald-500 text-white">
            {converting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowRight className="w-4 h-4 mr-1" />}
            Onaylı Kalemleri Siparişe Dönüştür
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
